import React, { useState, useEffect } from "react";
import { Paper, Typography, Grid2, Divider, FormControl, FormGroup, FormControlLabel, Checkbox, FormLabel, FormHelperText } from "@mui/material";
import { useForm, Controller } from 'react-hook-form';
import axios from "axios";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useNavigate, useLocation, useSearchParams } from "react-router-dom";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from 'zod';

import BreadCrumbs from "@components/breadCrumbs/breadCrumbs";
import CustomButton from "@components/button/customButton";
import ConfirmDialog from "@components/dialog/confirmDialog";
import SkeletonWrapper from "@components/skeleton/skeletonWrapper";
import ControlledTextField from "@components/inputs/controlledTextField";
import ControlledAutocomplete from "@components/inputs/controlledAutocomplete";

import { useSnackbar } from "@context/snackbarContext";

import { useAuthentication } from "@redux/authentication/hook";

import { AdminApiEndpoints } from "@configuration/apiEndpoints";

import { formatDateTime } from "@utils/formatter";

import { BANKS } from "@utils/constant";

const schema = z.object({
  bankName: z.string({invalid_type_error: 'Bank is required'}).min(1, 'Bank is required'),
  accountNumber: z.string().min(1, { message: 'Account No is required' }),

  isActivity: z.boolean(),
  isDonation: z.boolean(),

  /* view only */
  createdOn: z.string().optional(),
  createdBy: z.string().optional(),
})
.refine((data) => data.isActivity || data.isDonation, {
  message: "You must select at least one option (Activity or Donation)",
  path: ["checkboxGroup"], // Custom path for shared error
});

export default function BankForm() {
  const queryClient = useQueryClient()
  const openSnackbar = useSnackbar();
  const navigate = useNavigate()
  const { pathname } = useLocation();
  const { user } = useAuthentication();
  const pageAction = pathname.split("/")[3] || null; // get from pathname expected values ["create" || "view" || "edit"]
  const [searchParams] = useSearchParams();
  const id = searchParams.get("id");

  // console.log(user)

  const { handleSubmit, control, getValues, reset, formState: { errors } } = useForm({
    resolver: zodResolver(schema),
    values: {
      isActivity: true,
      isDonation: true
    }
  });

  const [openConfirmDialog, setOpenConfirmDialog] = useState(false)

  useEffect(()=>{
    if (((pageAction === "edit" || pageAction === "view") && !id) || (pageAction === "create" && id)) {
      onBack()
    }
    if (id) {
      fetchBankMutation.mutate(id)
    }
  },[pageAction])

  const fetchBankMutation = useMutation({
    mutationFn: (id) => {
      return axios.get(`${AdminApiEndpoints.getBank}/${id}`)
    },
    onSuccess: (res) => {
      const {createdOn, ...data} = res.data
      const createdOnFormatted = formatDateTime(createdOn)
      reset({...data, createdOn: createdOnFormatted})
    },
    onError: (err) => {
      openSnackbar("Failed to retrieve bank", "error");
    }
  });

  const createBankMutation = useMutation({
    mutationFn: (data) => {
      return axios.post(AdminApiEndpoints.createBank, {...data, by: user.username})
    },
    onSuccess: () => {
      setOpenConfirmDialog(false)
      queryClient.invalidateQueries({ queryKey: ['getBank'] })
      openSnackbar("Successfully created bank", "success");
      onBack()
    },
    onError: (err) => {
      setOpenConfirmDialog(false)
      if (err?.response) openSnackbar(err?.response?.data, "error");
      else openSnackbar("Failed to create bank", "error");
    }
  })

  const updateBankMutation = useMutation({
    mutationFn: (data) => {
      return axios.put(`${AdminApiEndpoints.updateBank}/${id}`, {...data, by: user.username})
    },
    onSuccess: () => {
      setOpenConfirmDialog(false)
      queryClient.invalidateQueries({ queryKey: ['getBank'] })
      openSnackbar("Successfully updated bank", "success");
      fetchBankMutation.mutate(id)
    },
    onError: (err) => {
      setOpenConfirmDialog(false)
      if (err?.response) openSnackbar(err?.response?.data, "error");
      else openSnackbar("Failed to update bank", "error");
    }
  })

  const onSubmit = () => {
    setOpenConfirmDialog(true)
  };

  const onConfirmSubmit = () => {
    const data = getValues();
    if(pageAction === "create")
      createBankMutation.mutate(data)
    else
      updateBankMutation.mutate(data)
  }

  const onBack = () => {
    navigate('/admin/bank-setup', { replace: true });
  }

  return (
    <div style={{display: 'flex', flexDirection: 'column', gap: "25px"}}>
      <BreadCrumbs />
      <SkeletonWrapper isLoading={fetchBankMutation.isPending} isError={fetchBankMutation.isError} errorMessage="Unable to load bank" disabled={["create"].includes(pageAction)}>
      <form onSubmit={handleSubmit(onSubmit)} style={{display: 'flex', flexDirection: 'column', gap: "25px"}}>
        <Paper
          sx={{
            p: "20px 25px",
            boxShadow: "none",
            borderRadius: "10px",
            border: "1px solid #E5E5E5",
          }}
        >
          <Grid2 container spacing={"20px"}>
            <Grid2 size={12}>
              <Typography fontSize={"0.9rem"} color="#202020" fontWeight={600}>Bank Detail</Typography>
            </Grid2>
            <Grid2 size={12}>
              <ControlledAutocomplete 
                name="bankName"
                control={control}
                label="Bank Name"
                disabled={fetchBankMutation.isPending || fetchBankMutation.isError || ["view"].includes(pageAction)}
                options={BANKS}
              />
            </Grid2>

            <Grid2 size={12}>
              <ControlledTextField 
                name="accountNumber"
                control={control}
                label="Account No"
                disabled={fetchBankMutation.isPending || fetchBankMutation.isError}
                readOnly={["view"].includes(pageAction)}
              />
            </Grid2>

            <Grid2 size={12}>
              <FormControl  component="fieldset" variant="standard">
                <FormLabel component="legend">Select Payment For?</FormLabel>
                <FormGroup>
                  <Controller
                    name="isActivity"
                    control={control}
                    render={({ field }) => (
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={field.value}
                          onChange={(e) => field.onChange(e.target.checked)}
                        />
                      }
                      label="Activity"
                    />
                  )}
                  />
                  <Controller
                    name="isDonation"
                    control={control}
                    render={({ field }) => (
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={field.value}
                          onChange={(e) => field.onChange(e.target.checked)}
                        />
                      }
                      label="Donation"
                    />
                  )}
                  />
                </FormGroup>
                {errors.checkboxGroup && (
                  <FormHelperText error>{errors.checkboxGroup.message}</FormHelperText>
                )}
              </FormControl>
            </Grid2>

            {["view", "edit"].includes(pageAction) && <>

            <Grid2 size={{ xs: 12 }}>
              <Divider />
            </Grid2>

            <Grid2 size={{ lg: 6, md: 12, sm: 12, xs: 12 }}>
              <ControlledTextField 
                name="createdOn"
                control={control}
                label="Created On"
                disabled={true}
              />
            </Grid2>

            <Grid2 size={{ lg: 6, md: 12, sm: 12, xs: 12 }}>
              <ControlledTextField 
                name="createdBy"
                control={control}
                label="Created By"
                disabled={true}
              />
            </Grid2>
            
            </>}

          </Grid2>
        </Paper>
        {["create", "edit"].includes(pageAction) && 
          <Paper
            sx={{
              p: "20px 25px",
              boxShadow: "none",
              borderRadius: "10px",
              border: "1px solid #E5E5E5",
              display: "flex",
              justifyContent: 'flex-end',
              gap: "10px"
            }}
          >
            <CustomButton type="submit" disabled={fetchBankMutation.isPending || fetchBankMutation.isError}>Submit</CustomButton>
          </Paper>
        }
      </form>
      </SkeletonWrapper>
      <ConfirmDialog
        open={openConfirmDialog}
        onClose={()=>setOpenConfirmDialog(false)}
        dialogTitle="Confirmation"
        dialogDescription="Submit bank?"
        onSubmit={()=>onConfirmSubmit()}
        loading={createBankMutation.isPending || updateBankMutation.isPending}
      />
    </div>
  );
}
